import type { DefaultServerService } from '@affine/core/modules/cloud/services/default-server';
import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import { type FrameworkProvider, LiveData, Service } from '@toeverything/infra';

// Share the code load across workspace scopes; a failed load is retried on the next use.
let implementation: Promise<typeof import('./initialize-provider')> | undefined;
const loadImplementation = () =>
  (implementation ??= import('./initialize-provider').catch(error => {
    implementation = undefined;
    throw error;
  }));

// AIProvider is one global; it serves the workspace that bound it last.
let boundService: AIProviderService | undefined;

/** Binds the global AIProvider to this workspace scope on use; cleanup is owned by the scope. */
export class AIProviderService extends Service {
  readonly ready$ = new LiveData(false);
  private binding?: Promise<void>;
  private cleanup?: () => void;
  private disposed = false;

  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly defaultServerService: DefaultServerService
  ) {
    super();
  }

  ensure(): Promise<void> {
    if (this.disposed)
      return Promise.reject(new Error('AI workspace disposed'));
    if (boundService === this) return Promise.resolve();
    return (this.binding ??= loadImplementation()
      .then(({ initializeAIProvider }) => {
        if (this.disposed) throw new Error('AI workspace disposed');
        const cleanup = initializeAIProvider(
          this.framework,
          this.workspaceServerService,
          this.defaultServerService
        );
        boundService?.release();
        this.cleanup = cleanup;
        boundService = this;
        this.ready$.next(true);
      })
      .finally(() => {
        this.binding = undefined;
      }));
  }

  private release(): void {
    this.cleanup?.();
    this.cleanup = undefined;
  }

  override dispose(): void {
    this.disposed = true;
    if (boundService === this) boundService = undefined;
    this.release();
    this.ready$.next(false);
    super.dispose();
  }
}

export function ensureAIProvider(framework: FrameworkProvider): Promise<void> {
  return framework.get(AIProviderService).ensure();
}

const hostFrameworks = new WeakMap<object, FrameworkProvider>();

/** Editor watchers bind the workspace without initializing any AI code. */
export function bindAIProviderHost(
  host: object,
  framework: FrameworkProvider
): void {
  hostFrameworks.set(host, framework);
}

export function ensureAIProviderForHost(host: object): Promise<void> {
  const framework = hostFrameworks.get(host);
  if (!framework)
    return Promise.reject(new Error('AI editor workspace unavailable'));
  return ensureAIProvider(framework);
}

const workspaceFrameworks = new Map<string, FrameworkProvider>();
export function bindAIProviderWorkspace(
  id: string,
  framework: FrameworkProvider
): () => void {
  workspaceFrameworks.set(id, framework);
  return () => {
    if (workspaceFrameworks.get(id) === framework)
      workspaceFrameworks.delete(id);
  };
}
export function ensureAIProviderForWorkspace(id: string): Promise<void> {
  const framework = workspaceFrameworks.get(id);
  if (!framework) return Promise.reject(new Error('AI workspace unavailable'));
  return ensureAIProvider(framework);
}

/** Observe already-requested initialization without turning an event listener into first use. */
export function observeAIProvider(
  framework: FrameworkProvider,
  observe: (provider: typeof import('./ai-provider').AIProvider) => () => void
): () => void {
  let current = true;
  let release: (() => void) | undefined;
  const subscription = framework
    .get(AIProviderService)
    .ready$.subscribe(ready => {
      if (!ready) return;
      void import('./ai-provider').then(({ AIProvider }) => {
        if (current) release = observe(AIProvider);
      });
    });
  return () => {
    current = false;
    subscription.unsubscribe();
    release?.();
  };
}

export async function openAIChatForHost(host: object): Promise<void> {
  const framework = hostFrameworks.get(host);
  if (!framework) throw new Error('AI editor workspace unavailable');
  await ensureAIProvider(framework);
  const [
    { ViewService },
    { WorkspaceService },
    { showWorkspaceCopilot },
    { chatPanelContextOwnerReady },
  ] = await Promise.all([
    import('@affine/core/modules/workbench/services/view'),
    import('@affine/core/modules/workspace'),
    import('@affine/core/desktop/pages/workspace/layouts/workspace-copilot-overlay-control'),
    import('@affine/core/desktop/pages/workspace/layouts/chat-panel-host-registry'),
  ]);
  const scope = {
    workspaceId: framework.get(WorkspaceService).workspace.id,
    viewId: framework.get(ViewService).view.id,
  };
  if (!showWorkspaceCopilot(scope).ok) throw new Error('AI chat unavailable');
  await chatPanelContextOwnerReady(scope, AbortSignal.timeout(5000));
}
